import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'

import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { rem } from 'polished'
import PaneDetailsSection from 'components/Common/Pane/PaneDetailsSection'
import List from 'components/Common/List'
import { mediaQueryUp } from 'components/utils/breakpoint'

import OfferPageRoomGallery from '../OfferPageRoomGallery'
import Pane from 'components/Common/Pane'
import useToggle from 'hooks/useToggle'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { ImageParams } from 'components/Common/ResponsiveImage'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import MultipleRoomsBookingPriceDetails from './MultipleRoomsBookingPriceDetails'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import { getPlural } from 'lib/string/pluralize'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import Group from 'components/utils/Group'
import TextLink from 'components/Luxkit/TextLink'
import MultipleRoomsInclusions from './MultipleRoomsInclusions'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import FlashHotelRoomDetailsModal from '../FlashHotel/FlashHotelMobileVariant/Modals/FlashHotelRoomDetailsModal'
import moment from 'moment'

const Container = styled(Pane)`
  position: relative;
`

const ContentGrid = styled.div`
  display: grid;
  grid-gap: ${rem(20)};
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: ${rem(20)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(400)} 1fr 0.5fr;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(12)};
  }

  ${mediaQueryUp.tablet} {
    grid-column: 1 / span 3;
  }

  ${mediaQueryUp.desktop} {
    text-align: left;
    grid-column: 1;

    > * + * {
      margin-top: 0;
    }
  }
`

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 2;
  padding: 0 ${rem(20)};

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    grid-column: 1 / span 3;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 2;
    grid-row: 1;
    padding: ${rem(20)} 0;
  }
`

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 3;
  padding: 0 ${rem(20)} ${rem(20)};

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
    grid-column: 1 / span 3;
    padding: ${rem(20)};
  }

  ${mediaQueryUp.desktop} {
    grid-column: 3;
    grid-row: 1;

    border-left: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const RoomImages = styled(OfferPageRoomGallery)`
  width: 100%;
`

const DetailsPane = styled.div`
  padding: ${rem(20)};

  > * + * {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    display: flex;

    > * {
      margin-top: 0;
      flex: 1 1 50%;

      &:nth-child(2) {
        /* First child is the close cross */
        padding-right: ${rem(30)};
      }

      &:last-child {
        padding-left: ${rem(30)};
      }
    }
  }
`

const DetailsDescription = styled(MarkdownRender)`
  margin-top: ${rem(12)};
`

const AmenityList = styled(List)`
  margin-top: ${rem(12)};
  margin-bottom: 0;

  ${mediaQueryUp.tablet} {
    columns: 2;
  }
`

interface Props extends MappedProps {
  offer: App.Offer;
  flightDestination?: App.Airport;
  room: App.RoomOccupants;
  isFlightsEnabled?: boolean;
  packageOption: OfferPackageOptionView;
  packageOptionSelectionsAmount?: number;
  isFetching?: boolean;
  isFlightsSelected?: boolean;
  buyNowBookLater: boolean;
  isUpgradable?: boolean;
  availablePackagesQuantity?: number;
  openRoomsModal: (room: App.RoomOccupants) => void;
  availableForPurchase?: boolean;
  checkIn?: string;
  duration?: number;
  airports?: Array<App.Airport>;
}

const imageProps: ImageParams = {
  mobileAspectRatio: '32:13',
  tabletAspectRatio: '35:12',
  desktopAspectRatio: '3:2',
  desktopWidth: '420px',
  quality: 'good',
}

function MultipleRoomsBookingOption(props: Props) {
  const {
    offer,
    checkIn,
    duration,
    airports,
    buyNowBookLater,
    room,
    isFlightsEnabled,
    packageOption,
    packageOptionSelectionsAmount = 0,
    isFetching,
    isFlightsSelected,
    isUpgradable,
    availablePackagesQuantity = 0,
    openRoomsModal,
    availableForPurchase,
  } = props

  const pkg = packageOption.package

  const [showDetails, onToggleDetails] = useToggle()

  const flightDestination = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const showModal = useContext(ModalContext)

  const isMobile = useScreenSizeOnly('mobile')
  const onShowRoomDetails = useCallback(() => {
    if (isMobile) {
      showModal(<FlashHotelRoomDetailsModal room={pkg.roomType} />)
    } else {
      onToggleDetails()
    }
  }, [isMobile, onToggleDetails, pkg.roomType, showModal])

  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, duration])

  return (
    <Container shadow="bottom-small" data-testid="package-option">
      <ScrollAnchor id={pkg.roomType?.id} vOffset={rem(80)} />
      <ContentGrid>
        <TitleSection>
          {!!pkg.roomType?.images?.length && <RoomImages
            room={pkg.roomType}
            imageProps={imageProps}
          />}
        </TitleSection>
        <DescriptionSection>
          <Group direction="vertical" gap={4}>
            {checkIn && packageOption.availableRooms === 1 &&
              <UrgencyLabel variant="critical1">Last {getPlural('room', packageOptionSelectionsAmount)}</UrgencyLabel>
            }
            {pkg.roomType?.name &&
              <Heading variant="heading5">{pkg.roomType.name}</Heading>
            }
            {pkg.roomType?.sizeSqm &&
              <BodyText variant="medium">
                <strong>Size:</strong> {pkg.roomType.sizeSqm} m&sup2;
              </BodyText>
            }
          </Group>
          <TextLink size="medium" onClick={onShowRoomDetails}>
            {showDetails ? 'Hide' : 'View'} room details
          </TextLink>

          <div>
            <Heading variant="heading6">{pkg.name}</Heading>
            {availableForPurchase && <FlashCancellationPolicies
              checkInDate={checkIn}
              propertyTimezoneOffset={offer.property?.timezoneOffset}
              cancellationPolicy={pkg.roomRate?.cancellationPolicy}
            />}
            {offer.type == OFFER_TYPE_ALWAYS_ON &&
              <TAOCancellationPolicies
                offerId={offer.id}
                useDynamicCancellationPolicies={offer.property?.useDynamicCancellationPolicies && !pkg.ignoreDynamicCancellationPolicy}
                timezoneOffset={offer.property?.timezoneOffset}
                uniqueKey={pkg.uniqueKey}
                checkInDate={checkIn}
                checkOutDate={checkOut}
                occupants={[room]}
                cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
                offerType={offer.type}
              />
            }
          </div>
          <MultipleRoomsInclusions
            offer={offer}
            packageOption={packageOption}
          />
        </DescriptionSection>
        <PriceSection>
          <MultipleRoomsBookingPriceDetails
            flightDestination={flightDestination}
            room={room}
            isFlightsEnabled={isFlightsEnabled}
            packageOption={packageOption}
            isFetching={isFetching}
            isFlightsSelected={isFlightsSelected}
            buyNowBookLater={buyNowBookLater}
            openRoomsModal={openRoomsModal}
            isUpgradable={isUpgradable}
            availablePackagesQuantity={availablePackagesQuantity}
          />
        </PriceSection>
      </ContentGrid>

      <PaneDetailsSection isOpen={showDetails} onToggle={onToggleDetails}>
        <DetailsPane>
          <div>
            <BodyText variant="large" weight="bold">Room facilities:</BodyText>
            <BodyText variant="medium" >
              <AmenityList>
                {pkg.roomType.amenities.map(amenity =>
                  <li key={`${amenity.group}-${amenity.id}`}>{amenity.description}</li>,
                )}
              </AmenityList>
            </BodyText>
          </div>
          <div>
            {pkg.roomPolicyDescription && <>
              <BodyText variant="medium" weight="bold">Room policies:</BodyText>
              <DetailsDescription fontSize="medium" content={pkg.roomPolicyDescription} type="compact" />
            </>}
          </div>
        </DetailsPane>
      </PaneDetailsSection>
      <LoadingIndicator visible={isFetching} floating opaque />
    </Container>
  )
}

type MappedProps = Pick<Props,
  'checkIn' |
  'duration' |
  'airports' |
  'availableForPurchase'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  airports: appState.geo.airports,
  availableForPurchase: isAvailableForPurchase(state, appState),
}), MultipleRoomsBookingOption)
